import type { Doc, Id } from "@convex/_generated/dataModel";

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export interface EmailListProps {
  emails: Doc<"emails">[];
  selectedId: Id<"emails"> | null;
  onSelect: (emailId: Id<"emails">) => void;
  status: "LoadingFirstPage" | "CanLoadMore" | "LoadingMore" | "Exhausted";
  onLoadMore: (numItems: number) => void;
  className?: string;
}

export function EmailList({ emails, selectedId, onSelect, status, onLoadMore, className }: EmailListProps) {
  return (
    <div className={`h-full w-full flex-col border-r border-black/10 dark:border-white/10 md:w-80 lg:w-96 ${className ?? "flex"}`}>
      <div className="shrink-0 p-4">
        <h2 className="text-lg font-bold text-black dark:text-white">Inbox</h2>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {emails.map((email) => {
          const senderName = email.from.includes("<")
            ? email.from.split("<")[0].trim()
            : email.from.split("@")[0];
          const timeAgo = formatRelativeTime(email.receivedAt);

          return (
            <button
              key={email._id}
              onClick={() => onSelect(email._id)}
              className={`w-full border-b border-black/5 px-4 py-3 text-left transition-colors dark:border-white/5 ${
                selectedId === email._id
                  ? "bg-brand-coral/5"
                  : "hover:bg-black/5 dark:hover:bg-white/5"
              }`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className={`truncate text-sm ${email.isRead ? "font-normal text-black/70 dark:text-white/70" : "font-bold text-black dark:text-white"}`}>
                  {senderName}
                </span>
                <span className="shrink-0 text-xs text-black/40 dark:text-white/40">{timeAgo}</span>
              </div>
              <div className={`truncate text-sm ${email.isRead ? "text-black/60 dark:text-white/60" : "text-black/80 dark:text-white/80"}`}>
                {email.subject}
              </div>
              {email.textBody && (
                <div className="truncate text-xs text-black/40 dark:text-white/40">
                  {email.textBody.slice(0, 80)}
                </div>
              )}
            </button>
          );
        })}
        {(status === "CanLoadMore" || status === "LoadingMore") && (
          <button
            onClick={() => onLoadMore(20)}
            disabled={status === "LoadingMore"}
            className="w-full py-3 text-sm text-brand-coral hover:underline disabled:opacity-50"
          >
            {status === "LoadingMore" ? "Loading..." : "Load more"}
          </button>
        )}
      </div>
    </div>
  );
}
